import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { useAuth } from './api/auth'
import Layout from './components/Layout'
import Login from './pages/Login'
import Overview from './pages/overview/Overview'
import ArticleAnalysis from './pages/analytics/ArticleAnalysis'
import InventoryReach from './pages/analytics/InventoryReach'
import Catalog from './pages/catalog/Catalog'
import Inventory from './pages/inventory/Inventory'
import GrundartikelDetail from './pages/grundartikel/GrundartikelDetail'
import { LegacyGrundartikelSkuRedirect } from './components/LegacyGrundartikelRedirect'
import StockList from './pages/stock/StockList'
import StockDetail from './pages/stock/StockDetail'
import OrderList from './pages/orders/OrderList'
import OrderDetail from './pages/orders/OrderDetail'
import CustomerList from './pages/customers/CustomerList'
import CustomerDetail from './pages/customers/CustomerDetail'
import InvoiceList from './pages/invoices/InvoiceList'
import InvoiceDetail from './pages/invoices/InvoiceDetail'
import ShippingList from './pages/shipping/ShippingList'
import ShippingDetail from './pages/shipping/ShippingDetail'
import Users from './pages/admin/Users'
import MarktplatzKonten from './pages/admin/MarktplatzKonten'
import PricingSettings from './pages/pricing/PricingSettings'
import PricingWorkspace from './pages/pricing/PricingWorkspace'
import VerkaufspreisPage from './pages/pricing/verkaufspreis/VerkaufspreisPage'
import RolloutPage from './pages/pricing/rollout/RolloutPage'
import { WarehouseGate } from './components/warehouse/WarehouseGate'
import WarehouseHome from './pages/warehouse/WarehouseHome'
import ErstaufnahmePage from './pages/warehouse/ErstaufnahmePage'
import InventurPage from './pages/warehouse/InventurPage'
import InventurDetailPage from './pages/warehouse/InventurDetailPage'
import InventurZaehlenPage from './pages/warehouse/InventurZaehlenPage'
import PlacesPage from './pages/warehouse/PlacesPage'
import ReceiptsPage from './pages/warehouse/ReceiptsPage'
import ReceiptDetailPage from './pages/warehouse/ReceiptDetailPage'
import EinlagernPage from './pages/warehouse/EinlagernPage'
import UmfahrenPage from './pages/warehouse/UmfahrenPage'
import { JournalRoute } from './pages/warehouse/JournalPage'
import LeiterListenPage from './pages/warehouse/LeiterListenPage'
import NullmengenPage from './pages/warehouse/NullmengenPage'
import NachschubPage from './pages/warehouse/NachschubPage'
import WorkersPage from './pages/warehouse/WorkersPage'
import LeerplatzPage from './pages/warehouse/LeerplatzPage'
import LagerNaviLayout from './pages/warehouse/lager-navi/LagerNaviLayout'
import RegalePage from './pages/warehouse/lager-navi/RegalePage'
import NaviSuchePage from './pages/warehouse/lager-navi/SuchePage'
import { Weiterleitung } from './pages/warehouse/lager-navi/Weiterleitung'
import { ErstaufnahmeModuleGate } from './components/warehouse/ErstaufnahmeModuleGate'
import PriostahlClassificationPage from './pages/warehouse/PriostahlClassificationPage'
import HandregalPage from './pages/warehouse/HandregalPage'
import SicherungPage from './pages/warehouse/SicherungPage'
import PurchaseOrders from './pages/purchase/PurchaseOrders'
import PurchaseOrderDetail from './pages/purchase/PurchaseOrderDetail'
import PurchaseDraftRedirect from './pages/purchase/PurchaseDraftRedirect'
import ServiceCasesPage from './pages/product-knowledge/ServiceCasesPage'

const PriostahlBestandPage = lazy(() => import('./pages/priostahl-bestand/PriostahlBestandPage'))

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isLoggedIn } = useAuth()
  if (!isLoggedIn) return <Navigate to="/login" replace />
  return <>{children}</>
}

export function ArticleGeneralGate({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()
  if (!user?.permissions?.article_general) {
    return (
      <div className="p-8 max-w-lg mx-auto text-center space-y-2">
        <div className="text-lg font-bold text-red-700">Keine Berechtigung</div>
        <div className="text-sm text-muted">
          Fuer Produktwissen und Servicefaelle fehlt die Artikel-Berechtigung.
        </div>
      </div>
    )
  }
  return <>{children}</>
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/priostahl-bestand" element={<Suspense fallback={<p role="status">Lagerbestand wird geladen …</p>}><PriostahlBestandPage /></Suspense>} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/overview" replace />} />
          <Route path="overview" element={<Overview />} />
          <Route path="analytics/articles" element={<ArticleAnalysis />} />
          <Route path="analytics/reach" element={<InventoryReach />} />
          <Route path="catalog" element={<Catalog />} />
          <Route path="catalog/:sku" element={<GrundartikelDetail />} />
          <Route path="inventory" element={<Inventory />} />
          <Route path="product-knowledge/service-cases" element={(
            <ArticleGeneralGate><ServiceCasesPage /></ArticleGeneralGate>
          )} />
          <Route path="product-knowledge" element={<Navigate to="/product-knowledge/service-cases" replace />} />
          <Route path="grundartikel" element={<Navigate to="/catalog" replace />} />
          <Route path="grundartikel/:sku" element={<LegacyGrundartikelSkuRedirect />} />
          <Route path="stock" element={<StockList />} />
          <Route path="einkauf" element={<PurchaseOrders />} />
          <Route path="einkauf/:id" element={<PurchaseOrderDetail />} />
          <Route path="einkauf/:id/entwurf" element={<PurchaseDraftRedirect />} />
          <Route path="stock/:sku" element={<StockDetail />} />
          <Route path="orders" element={<OrderList />} />
          <Route path="orders/:id" element={<OrderDetail />} />
          <Route path="customers" element={<CustomerList />} />
          <Route path="customers/:id" element={<CustomerDetail />} />
          <Route path="invoices" element={<InvoiceList />} />
          <Route path="invoices/:id" element={<InvoiceDetail />} />
          <Route path="shipping" element={<ShippingList />} />
          <Route path="shipping/:id" element={<ShippingDetail />} />
          <Route path="preise" element={<PricingWorkspace />} />
          <Route path="preise/verkaufspreis" element={<VerkaufspreisPage />} />
          <Route path="preise/einstellungen" element={<PricingSettings />} />
          <Route path="preise/rollout" element={<RolloutPage />} />
          {/* C-I8/RA-WI8: Route-Guards spiegeln die Backend-Matrix (guards.ts hasWarehouseLevel)
              auf READ-Level — Inventur-Liste/Detail und Wareneingang-Liste/Detail sind
              GET-Routen mit 'general' im Backend (routes.ts), nur die enthaltenen Aktionen
              (Session anlegen/abschliessen/Zweitzaehlung, WE-Auftrag anlegen) sind 'lead',
              das Zaehlen selbst ist 'count'. Ein reiner Leiter (kein 'count') muss die Listen
              oeffnen und Sessions anlegen/abschliessen koennen — die Welle-W-Fassung hatte das
              faelschlich auf 'count'/'book' verengt (MEINE Regression, Re-Audit RA-WI8). Die
              Aktions-Buttons selbst bleiben inline auf ihrem jeweiligen Level gesperrt
              (InventurPage/InventurDetailPage: warehouse.lead/warehouse.count;
              ReceiptsPage: warehouse.lead). */}
          <Route path="warehouse" element={<WarehouseGate level="general"><WarehouseHome /></WarehouseGate>} />
          {/* Erstaufnahme (Modul ⑧): Board 'general' lesbar; Etiketten-erzeugen/Abgleich inline lead-gated. */}
          <Route path="warehouse/erstaufnahme" element={<WarehouseGate level="general"><ErstaufnahmeModuleGate><ErstaufnahmePage /></ErstaufnahmeModuleGate></WarehouseGate>} />
          {/* Lager-Navi (Plan 2026-10-23 S3): Pfad-Reiter unter einem Layout. */}
          <Route path="warehouse/lager-navi" element={<WarehouseGate level="general"><LagerNaviLayout /></WarehouseGate>}>
            <Route index element={<NaviSuchePage />} />
            <Route path="regale" element={<RegalePage />} />
            <Route path="leere-plaetze" element={<WarehouseGate level="lead"><LeerplatzPage /></WarehouseGate>} />
            <Route path="plaetze" element={<PlacesPage />} />
          </Route>
          {/* Alte URLs: nur noch Weiterleitung in die Lager-Navi-Reiter, Query bleibt. */}
          <Route path="warehouse/leerplatz" element={<Weiterleitung to="/warehouse/lager-navi/leere-plaetze" />} />
          <Route path="warehouse/inventur" element={<WarehouseGate level="general"><InventurPage /></WarehouseGate>} />
          <Route path="warehouse/inventur/:id" element={<WarehouseGate level="general"><InventurDetailPage /></WarehouseGate>} />
          <Route path="warehouse/inventur/:id/zaehlen" element={<WarehouseGate level="count"><InventurZaehlenPage /></WarehouseGate>} />
          <Route path="warehouse/plaetze" element={<Weiterleitung to="/warehouse/lager-navi/plaetze" />} />
          <Route path="warehouse/wareneingang" element={<WarehouseGate level="general"><ReceiptsPage /></WarehouseGate>} />
          <Route path="warehouse/wareneingang/:id" element={<WarehouseGate level="general"><ReceiptDetailPage /></WarehouseGate>} />
          <Route path="warehouse/einlagern" element={<WarehouseGate level="book"><EinlagernPage /></WarehouseGate>} />
          <Route path="warehouse/umfahren" element={<WarehouseGate level="book"><UmfahrenPage /></WarehouseGate>} />
          <Route path="warehouse/journal" element={<WarehouseGate level="general"><JournalRoute /></WarehouseGate>} />
          <Route path="warehouse/leiter" element={<WarehouseGate level="lead"><LeiterListenPage /></WarehouseGate>} />
          <Route path="warehouse/nullmengen" element={<WarehouseGate level="lead"><NullmengenPage /></WarehouseGate>} />
          {/* Backend-Gate der Fillup-GET-Endpoints ist 'general' (routes.ts) — nur der
              Storno-Button ist inline lead-gated (Muster ReceiptsPage: general Route + lead Aktion). */}
          <Route path="warehouse/nachschub" element={<WarehouseGate level="general"><NachschubPage /></WarehouseGate>} />
          <Route path="warehouse/mitarbeiter" element={<WarehouseGate level="lead"><WorkersPage /></WarehouseGate>} />
          <Route path="warehouse/priostahl-classification" element={<WarehouseGate level="lead"><PriostahlClassificationPage /></WarehouseGate>} />
          <Route path="warehouse/handregal" element={<WarehouseGate level="general"><HandregalPage /></WarehouseGate>} />
          <Route path="warehouse/sicherung" element={<WarehouseGate level="admin"><SicherungPage /></WarehouseGate>} />
          <Route path="dashboard" element={<Navigate to="/overview" replace />} />
          <Route path="admin/users" element={<Users />} />
          <Route path="admin/marktplatzkonten" element={<MarktplatzKonten />} />
        </Route>
        <Route path="*" element={<Navigate to="/overview" replace />} />
      </Routes>
    </BrowserRouter>
  )
}
